import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    getAuth,
    PhoneAuthProvider,
    signInWithPhoneNumber,
    signInWithCredential,
    RecaptchaVerifier
} from 'firebase/auth';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import firebaseApp from '../../../../../firebaseConfig';
import { toast, ToastContainer } from 'react-toastify';
import CustomToast from '../../../common/CustomToast.js';
import 'react-toastify/dist/ReactToastify.css';
import DialogLoader from '../../../common/DialogLoader';
import ResultModal from '../../../common/ResultModal';
import { useDictionary } from '../../../../../utilities/hooks/UseDictionary.js';
import { RECAPTCHA_SITE_KEY } from '../../../../../config.js';
import { verificationPhoneNumber } from '../networking.js';

const OtpPopup = ({
                      setShowModal,
                      onActionFinished,
                      title,
                      message,
                      phoneNumber
                  }) => {
    const [status, setStatus] = useState(null);
    const [resultMessage, setResultMessage] = useState(null);
    const [opacity, setOpacity] = useState(0);
    const [showLoader, setLoaderVisibility] = useState(false);
    const [verificationId, setVerificationId] = useState(null);
    const [code, setCode] = useState('');
    const dictionary = useDictionary();

    useEffect(() => {
        setTimeout(() => {
            setOpacity(1);
        }, 50);
        initializeAppCheck(firebaseApp, {
            provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
            isTokenAutoRefreshEnabled: true
        });
        sendOtp();
    }, []);

    const showToast = (errorCode, message, error=true) => {
        if(error) {
            toast.error(<CustomToast message={message} />, {
                position: 'top-right',
                autoClose: 5000,
                toastId: `otp-${errorCode}`,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                closeButton: false,
                icon: false,
                className: 'toast-white-warning',
            });
        } else {
            toast.success(<CustomToast message={message} />, {
                position: 'top-right',
                autoClose: 5000,
                toastId: `otp-${errorCode}`,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                closeButton: false,
                icon: false,
                className: 'toast-white-success',
            });
        }
    };

    const sendOtp = () => {
        setLoaderVisibility(true);
        const auth = getAuth(firebaseApp);

        // Initialize RecaptchaVerifier only if it's not already created
        if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
                size: 'invisible',
                callback: (token) => {},
                'expired-callback': () => {
                    showToast('expired', dictionary.recaptcha_expired || 'Recaptcha expired. Please try again.');
                },
            });
        }

        const appVerifier = window.recaptchaVerifier;

        const phoneNumberWithCountryCode = `+${phoneNumber}`;

        signInWithPhoneNumber(auth, phoneNumberWithCountryCode, appVerifier)
            .then((confirmationResult) => {
                setVerificationId(confirmationResult.verificationId);
                setLoaderVisibility(false);
            })
            .catch((error) => {
                const { code: errorCode } = error;
                const errorMessages = {
                    'auth/invalid-phone-number': dictionary.invalid_phone_number || 'The mobile number you entered is invalid.',
                    'auth/quota-exceeded': dictionary.quota_exceeded || 'Too many attempts. Please try again later.',
                };

                const defaultErrorMessage = dictionary.general_error || 'An unexpected error occurred. Please try again later.';
                showToast(errorCode, errorMessages[errorCode] || defaultErrorMessage);
                setLoaderVisibility(false);
            });
    };


    const verifyOtp = () => {
        setLoaderVisibility(true);
        const auth = getAuth(firebaseApp);
        const credential = PhoneAuthProvider.credential(verificationId, code);

        signInWithCredential(auth, credential)
            .then((response) => {
                return response.user.getIdToken().then((idToken) => {
                    return verificationPhoneNumber({
                        'phone-token': idToken,
                    });
                }).then(() => {
                    setResultMessage(dictionary.phone_number_verified_successfully || 'Mobile number verified successfully')
                    onActionComplete();
                    setLoaderVisibility(false);
                }).catch((error) => {
                    showToast(400, error?.message || dictionary.otp_verification_failed || 'Failed to verify the an OTP');
                    setLoaderVisibility(false);
                    onActionFail();
                });
            })
            .catch((error) => {
                setLoaderVisibility(false);
                const { code: errorCode } = error;
                const errorMessages = {
                    'auth/invalid-verification-code': dictionary.invalid_otp || 'The verification code you entered is incorrect. Please try again or request a new code.',
                    'auth/expired-action-code': dictionary.otp_expired || 'The verification code has expired. Please request a new one.',
                    'auth/invalid-phone-number': dictionary.invalid_phone_number || 'The mobile number you entered is invalid.',
                    'auth/quota-exceeded': dictionary.otp_quota_exceeded || 'Too many requests made. Please try again later.',
                    'auth/too-many-requests': dictionary.otp_too_many_requests || 'Too many attempts. Please try again later.',
                    'auth/network-request-failed': dictionary.network_error || 'Network error. Please check your connection and try again.',
                };

                const defaultErrorMessage = dictionary.general_error || 'An unexpected error occurred. Please try again later.';
                showToast(errorCode, errorMessages[errorCode] || defaultErrorMessage);
                if (!errorMessages[errorCode]) onActionFail();
            });
    };

    const onActionComplete = () => {
        setLoaderVisibility(false);
        setStatus('success');
        setTimeout(() => {
            setShowModal(false);
            onActionFinished('success');
        }, 2000);
    };

    const onActionFail = () => {
        setTimeout(() => {
            setShowModal(false);
            onActionFinished('error');
        }, 2000);
    };

    const handleChange = (event, index) => {
        let newCode = code.split('');
        newCode[index] = event.target.value;
        setCode(newCode.join(''));

        if (event.target.value && index < 5) {
            document.getElementById(`otp-input-${index + 1}`).focus();
        }
    };

    useEffect(() => {
        if (code.length === 6) {
            verifyOtp();
        }
    }, [code]);

    return status !== null ? (
        <ResultModal opacity={opacity} message={resultMessage} type={status} />
    ) : (
        <div className="dialog-wrapper otp-wrapper" style={{ opacity: opacity, zIndex: '11' }}>
            <div className="dialog-container">
                <div className="dialog-title-container">
                    <h3 className='popup-title'>{dictionary.Confirm_your_phone}</h3>
                    <div className="dialog-close-button" onClick={() => setShowModal(false)} />
                </div>
                <div className="wrapper-section">
                    <h3 className="otp-text-messages">{title} <span>+{phoneNumber}</span></h3>
                    <div className="otp-text-messages">{message}</div>
                </div>

                {!status && (
                    <>
                        <div className={`input-container items-center flex-direction-column gap-16`}>
                            <div className="otp-row gap-16">
                                {[0, 1, 2].map((_, index) => (
                                    <input
                                        key={index}
                                        id={`otp-input-${index}`}
                                        type="text"
                                        maxLength={1}
                                        onChange={(event) => handleChange(event, index)}
                                        value={code[index] || ''}
                                    />
                                ))}
                            </div>
                            <div className="otp-row gap-16">
                                {[3, 4, 5].map((_, index) => (
                                    <input
                                        key={index + 3}
                                        id={`otp-input-${index + 3}`}
                                        type="text"
                                        maxLength={1}
                                        onChange={(event) => handleChange(event, index + 3)}
                                        value={code[index + 3] || ''}
                                    />
                                ))}
                            </div>
                        </div>
                        <h3 className="wrapper-section" style={{ marginBottom: 64 }}>
                            <p className="resend-message">{dictionary.did_not_receive_the_verification_code}<span className="resend-button" onClick={sendOtp}>{dictionary.Resend}</span></p>
                        </h3>
                    </>
                )}

                <DialogLoader showLoader={showLoader} />
                <ToastContainer />
            </div>
        </div>
    );
};

OtpPopup.propTypes = {
    setShowModal: PropTypes.func.isRequired,
    onActionFinished: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string.isRequired,
};

export default OtpPopup;
